/**
 * Shared helpers for session row/card.
 */
import { toLocalDateString } from '@/utils/format-date-utils';
import { resolveListDateRange } from '@/utils/list-date-range-utils';
import type { AttendanceStatus } from '@/types/attendance.types';
import { SESSION_TAB_TO_TYPES } from '@/constants/shared-catalog';
import type {
  ListSessionGroupsParams,
  SessionGroup,
  SessionGroupChild,
  SessionStatus,
  SessionTab,
  SessionWithParticipants,
} from '@/types/inperson/session.types';
import {
  JOINABLE_SESSION_STATUSES,
  SESSION_GROUP_STATUS_CONFIG,
  SESSION_TYPE_CONFIG,
  type SessionFilters,
} from '@/components/sessions/session-configs';
import { getErrorMessage } from '@/utils/errors-utils';
import { expandNotCompletedSessionStatuses, getInPersonStatusConfig } from '@/utils/common-utils';

export {
  getInPersonStatusConfig,
  getInPersonStatusKeysForTypes,
  getSessionFormatLabel,
} from '@/utils/common-utils';

/** Flattened session row used by the in-person list (group child or standalone). */
export interface SessionListItem {
  id: string;
  sessionDate: Date | string;
  startTime: string;
  endTime: string;
  type:
    | 'homework_help'
    | 'individual_tutoring'
    | 'workshop'
    | 'whole_class'
    | 'group_tutoring'
    | 'quick_help_desk'
    | 'information_meeting_in_person';
  status: SessionStatus;
  mode?: 'single' | 'recurring';
  recurrenceType?: string;
  recurrenceGroupId?: string | null;
  establishmentName?: string | null;
  establishment?: { name: string };
  establishmentId?: string;
  levelNames?: string[];
  subjectNames?: string[];
  _permissions?: Record<string, boolean>;
}

/** RBAC flags for a session row/card. */
export interface SessionItemPageActions {
  canEditRow?: boolean;
  canCancelRow?: boolean;
  canViewReport?: boolean;
  canViewReportInformation?: boolean;
  canJoinInformationMeeting?: boolean;
  canDeleteRow?: boolean;
  canManageAttendance?: boolean;
  canExportCsv?: boolean;
}

/** Student plus assignment source (`manual` / `auto_*`). */
export type StudentWithSource = {
  student: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    photoUrl: string | null;
    userProfiles?: Array<{ levelId?: string | null }>;
  };
  source: string;
};

/** Present / absent / excused / pending tallies (volunteers excluded). */
export type AttendanceCount = {
  present: number;
  absent: number;
  excused: number;
  pending: number;
};

/** Minimal student identity for attendance grouping. */
export type StudentUser = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  photoUrl: string | null;
  userProfiles?: Array<{ levelId?: string | null }>;
};

/** Person chip shape (id, name, optional photo / level). */
export type UserForDisplay = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  displayName?: string | null;
  photoUrl: string | null;
  userProfiles?: Array<{ levelId?: string | null }>;
};
/** Central error copy from {@link getErrorMessage}. */
export function getInPersonTableApiError(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** Success toast after cancelling a group (mentions skipped children when any). */
export function buildSessionGroupCancelSuccessMessage(
  cancelledCount: number,
  skippedCount: number,
): string {
  let msg = `${cancelledCount} séance(s) annulée(s) avec succès !`;
  if (skippedCount > 0) {
    msg = `${cancelledCount} séance(s) annulée(s), ${skippedCount} ignorée(s).`;
  }
  return msg;
}

/** `HH:mm` from an API time string, or `--:--`. */
export const formatSessionTime = (time: string): string => {
  if (!time) return '--:--';
  return time.slice(0, 5);
};

/** Duration in minutes; `null` when end is missing or before start. */
export const calcDurationMinutes = (start: string, end?: string | null): number | null => {
  if (!end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = eh * 60 + em - (sh * 60 + sm);
  return diff > 0 ? diff : null;
};

/** Parse `YYYY-MM-DD` as a local date (avoids UTC-shift from `new Date(iso)`). */
export const parseSessionGroupDate = (sessionDate: string): Date => {
  const [year, month, day] = sessionDate.split('-').map(Number);
  if (!year || !month || !day) return new Date(sessionDate);
  return new Date(year, month - 1, day);
};

/** Establishment name from the flattened field or the nested object. */
export const getEstablishmentLabel = (item: {
  establishmentName?: string | null;
  establishment?: { name: string };
}): string => item.establishmentName ?? item.establishment?.name ?? 'Non défini';

/** Attendance-button tooltip from permission + session status. */
export const getAttendanceTooltip = (
  onManageAttendance: ((id: string) => void) | undefined,
  isDisabled: boolean,
  status: SessionStatus,
): string => {
  if (!onManageAttendance) return getErrorMessage('actionUnavailable');
  if (!isDisabled) return 'Gérer les présences';
  if (status === 'COMPLETED') return getErrorMessage('sessionEnded');
  if (status === 'CANCELLED') return getErrorMessage('sessionCancelled');
  return getErrorMessage('sessionClosed');
};

/** Report-button tooltip (only in-progress / completed can open the editor). */
export const getSessionReportTooltip = (isLoading: boolean, status: SessionStatus): string => {
  if (isLoading) return 'Chargement';
  if (status === 'IN_PROGRESS' || status === 'COMPLETED') return 'Éditer le compte rendu';
  if (status === 'PLANNED') return getErrorMessage('sessionNotStarted');
  if (status === 'CLOSED') return getErrorMessage('sessionClosed');
  return getErrorMessage('sessionCancelled');
};

/** Edit-button tooltip when the session is in a terminal status. */
export const getSessionEditTooltip = (isDisabled: boolean, status: SessionStatus): string => {
  if (!isDisabled) return 'Modifier la séance';
  if (status === 'COMPLETED') return getErrorMessage('cannotEditSessionEnded');
  if (status === 'NOT_COMPLETED' || status === 'PARTICIPANTS_ABSENT')
    return getErrorMessage('cannotEditSessionNotHeld');
  if (status === 'CANCELLED') return getErrorMessage('cannotEditSessionCancelled');
  if (status === 'CLOSED') return getErrorMessage('cannotEditSessionClosed');
  return getErrorMessage('cannotEdit');
};

/** Cancel-button tooltip; only `PLANNED` sessions can be cancelled. */
export const getSessionCancelTooltip = (isDisabled: boolean, status: SessionStatus): string => {
  if (!isDisabled) return 'Annuler la séance';
  if (status === 'CANCELLED') return getErrorMessage('cannotCancelAlreadyCancelled');
  if (status === 'CLOSED') return getErrorMessage('cannotCancelSessionClosed');
  if (status === 'COMPLETED') return getErrorMessage('cannotCancelSessionEnded');
  if (status === 'NOT_COMPLETED' || status === 'PARTICIPANTS_ABSENT')
    return getErrorMessage('cannotCancelSessionNotHeld');
  return getErrorMessage('cannotCancel');
};

/** Disable / visibility flags for attendance, report, edit, cancel, join. */
export const deriveSessionActionFlags = (
  session: SessionListItem,
  onManageAttendance: ((id: string) => void) | undefined,
  isLoadingReport: boolean,
) => {
  const isTerminated = session.status === 'CLOSED' || session.status === 'CANCELLED';
  const isInformationMeeting = session.type === 'information_meeting_in_person';
  const editDisabled =
    isTerminated ||
    (isInformationMeeting &&
      (session.status === 'COMPLETED' ||
        session.status === 'NOT_COMPLETED' ||
        session.status === 'PARTICIPANTS_ABSENT'));
  return {
    isTerminated,
    attendanceDisabled:
      !onManageAttendance || session.status === 'CLOSED' || session.status === 'CANCELLED',
    reportDisabled:
      isLoadingReport || (session.status !== 'IN_PROGRESS' && session.status !== 'COMPLETED'),
    editDisabled,
    cancelDisabled: session.status !== 'PLANNED',
    showJoinMeeting:
      session.type === 'information_meeting_in_person' &&
      JOINABLE_SESSION_STATUSES.has(session.status),
  };
};

/** Custom range wins; otherwise expand the period preset to `dateFrom` / `dateTo`. */
export const getDateRangeFromFilters = (
  filters: SessionFilters,
): { dateFrom: Date | undefined; dateTo: Date | undefined } => {
  const { dateFrom, dateTo } = resolveListDateRange(filters, {
    periodEnd: 'endOfPeriod',
  });
  return { dateFrom, dateTo };
};

/** Wrap a standalone information meeting as a one-child group for the same list. */
export const toSyntheticInformationGroup = (session: SessionListItem): SessionGroup => {
  const sessionDate =
    session.sessionDate instanceof Date
      ? toLocalDateString(session.sessionDate)
      : String(session.sessionDate).slice(0, 10);

  let status: SessionGroup['status'] = 'COMPLETED';
  if (session.status === 'CANCELLED') {
    status = 'CANCELLED';
  } else if (session.status === 'IN_PROGRESS' || session.status === 'TO_LAUNCH') {
    status = 'IN_PROGRESS';
  } else if (session.status === 'PLANNED') {
    status = 'PLANNED';
  }

  return {
    id: session.id,
    createdAt: '',
    updatedAt: '',
    companyId: '',
    establishmentId: '',
    schoolYearId: '',
    sessionDate,
    startTime: session.startTime,
    endTime: session.endTime,
    type: session.type,
    sessionFormat: 'in_person',
    establishmentName: session.establishmentName ?? session.establishment?.name ?? null,
    status,
    sessions: [
      {
        id: session.id,
        sessionGroupId: session.id,
        status: session.status,
        sessionDate,
        startTime: session.startTime,
        endTime: session.endTime,
        type: session.type,
        establishmentId: '',
        companyId: '',
      },
    ],
  };
};

/** Types sent to the API: chip ∩ tab map, or all allowed-tab types on `all`. */
export function resolveEffectiveTypes(
  activeTab: 'all' | SessionTab | undefined,
  allowedTabIds: SessionTab[] | undefined,
  selectedTypes: string[],
): string[] | undefined {
  if (!activeTab || activeTab === 'all') {
    if (selectedTypes.length > 0) return selectedTypes;
    if (allowedTabIds && allowedTabIds.length > 0) {
      return allowedTabIds.flatMap((id) => SESSION_TAB_TO_TYPES[id] ?? []);
    }
    return undefined;
  }
  const tabTypes = SESSION_TAB_TO_TYPES[activeTab] ?? [];
  if (selectedTypes.length > 0) {
    const intersection = selectedTypes.filter((t) => (tabTypes as string[]).includes(t));
    if (intersection.length > 0) return intersection;
    return tabTypes;
  }
  if (tabTypes.length > 0) return tabTypes;
  return undefined;
}

/** True when standalone info-meetings must be merged into the group list. */
export function shouldMergeStandaloneInformation(
  hasExternalSessions: boolean,
  isInformationTab: boolean,
  activeTab: 'all' | SessionTab | undefined,
  allowedTabIds: SessionTab[] | undefined,
): boolean {
  return (
    !hasExternalSessions &&
    !isInformationTab &&
    (activeTab === 'all' || activeTab == null) &&
    (allowedTabIds == null || allowedTabIds.includes('information-meeting'))
  );
}

/** Append synthetic info-meeting groups and sort by date then start time. */
export function mergeGroupsWithStandalone(
  groups: SessionGroup[],
  standalone: SessionListItem[],
): SessionGroup[] {
  if (standalone.length === 0) return groups;
  const existingIds = new Set(groups.map((g) => g.id));
  const synthetic = standalone
    .map((s) => toSyntheticInformationGroup(s))
    .filter((g) => !existingIds.has(g.id));
  return [...groups, ...synthetic].sort((a, b) => {
    const byDate = a.sessionDate.localeCompare(b.sessionDate);
    if (byDate !== 0) return byDate;
    return a.startTime.localeCompare(b.startTime);
  });
}

/** True when any filter differs from the table defaults. */
export function areSessionFiltersActive(
  filters: SessionFilters,
  defaults: SessionFilters,
): boolean {
  return (
    filters.types.length > 0 ||
    filters.statuses.length > 0 ||
    filters.dateRange !== defaults.dateRange ||
    !!filters.customDateFrom ||
    !!filters.customDateTo ||
    !!filters.establishmentId ||
    !!filters.subjectId ||
    !!filters.levelId ||
    !!filters.recurrenceType ||
    !!filters.animatorUserId
  );
}

/** Status, first active child, and counts for a group row. */
export const deriveSessionGroupDisplay = (group: SessionGroup) => {
  const displayStatus =
    group.type === 'information_meeting_in_person'
      ? (group.sessions[0]?.status ?? group.status)
      : group.status;
  const statusConfig =
    group.type === 'information_meeting_in_person'
      ? getInPersonStatusConfig(displayStatus, group.type)
      : (SESSION_GROUP_STATUS_CONFIG[group.status] ?? SESSION_GROUP_STATUS_CONFIG.PLANNED);
  const firstActive = group.sessions.find((s) => s.status !== 'CANCELLED') ?? group.sessions[0];
  const firstActiveId = firstActive?.id ?? '';
  const totalChildren = group.sessions.length;
  const activeGroupCount = group.sessions.filter((s) => s.status !== 'CANCELLED').length;
  const isTerminated =
    group.status === 'CANCELLED' ||
    (group.type === 'information_meeting_in_person' &&
      (displayStatus === 'COMPLETED' ||
        displayStatus === 'NOT_COMPLETED' ||
        displayStatus === 'PARTICIPANTS_ABSENT' ||
        displayStatus === 'CLOSED' ||
        displayStatus === 'CANCELLED'));

  return {
    displayStatus,
    statusConfig,
    firstActive,
    firstActiveId,
    totalChildren,
    groupCount: totalChildren,
    activeGroupCount,
    hasMultipleReports: activeGroupCount > 1,
    isTerminated,
    sessionDate: parseSessionGroupDate(group.sessionDate),
  };
};

/** Group edit tooltip (terminated, no active child, or past the edit window). */
export const getGroupEditTooltip = (
  editDisabled: boolean,
  isTerminated: boolean,
  firstActiveId: string,
  isOutsidePastWindow: boolean,
  status?: string,
): string => {
  if (!editDisabled) return 'Modifier la séance';
  if (status === 'COMPLETED') return getErrorMessage('cannotEditSessionEnded');
  if (status === 'NOT_COMPLETED' || status === 'PARTICIPANTS_ABSENT')
    return getErrorMessage('cannotEditSessionNotHeld');
  if (isTerminated) return getErrorMessage('cannotEditClosedOrCancelled');
  if (!firstActiveId) return getErrorMessage('noActiveSession');
  if (isOutsidePastWindow) return getErrorMessage('editDeadlinePassed');
  return getErrorMessage('cannotEdit');
};

/** Group attendance-button tooltip. */
export const getGroupAttendanceTooltip = (disabled: boolean): string =>
  disabled ? getErrorMessage('attendanceUnavailable') : 'Gérer les présences';

/** Group report tooltip; mentions one report per child when several are active. */
export const getGroupReportTooltip = (
  reportDisabled: boolean,
  groupStatus: SessionGroup['status'],
  hasMultipleReports: boolean,
): string => {
  if (reportDisabled) {
    if (groupStatus === 'PLANNED') return getErrorMessage('sessionNotStarted');
    if (groupStatus === 'CANCELLED') return getErrorMessage('sessionCancelled');
    return getErrorMessage('reportUnavailableShort');
  }
  return hasMultipleReports ? 'Voir les comptes rendus (un par groupe)' : 'Éditer le compte rendu';
};

/** Group cancel tooltip (cancels every child). */
export const getGroupCancelTooltip = (
  cancelDisabled: boolean,
  groupStatus: SessionGroup['status'],
): string => {
  if (!cancelDisabled) return 'Annuler toutes les séances du groupe';
  if (groupStatus === 'CANCELLED') return getErrorMessage('groupAlreadyCancelled');
  return getErrorMessage('cannotCancelEndedSessions');
};

/** Child-row cancel tooltip (this session only). */
export const getChildCancelTooltip = (isCancelled: boolean, isTerminated: boolean): string => {
  if (isCancelled) return getErrorMessage('sessionAlreadyCancelled');
  if (isTerminated) return getErrorMessage('cannotCancelEndedSession');
  return 'Annuler uniquement cette séance';
};

/** `N groupes · M actifs` when some children are cancelled. */
export const formatSessionGroupCountLabel = (
  total: number,
  activeCount: number,
  cancelledCount: number,
): string => {
  const groups = `${total} groupe${total > 1 ? 's' : ''}`;
  if (cancelledCount === 0) return groups;
  return `${groups} · ${activeCount} actif${activeCount > 1 ? 's' : ''}`;
};

/** Type guard: expanded row data is a group (`sessions` + `sessionFormat`). */
export const isSessionGroup = (data: unknown): data is SessionGroup =>
  typeof data === 'object' &&
  data !== null &&
  'sessions' in data &&
  Array.isArray((data as SessionGroup).sessions) &&
  'sessionFormat' in data;

/** Comma-separated tutor/volunteer names on a group child. */
export const tutorLabelOf = (child: SessionGroupChild): string => {
  const tutors = (child.participants ?? []).filter(
    (p) => p.role === 'tutor' || p.role === 'volunteer',
  );
  return tutors
    .map((t) => `${t.user.firstName ?? ''} ${t.user.lastName ?? ''}`.trim())
    .filter(Boolean)
    .join(', ');
};

/** Map tutor id → students paired with them on this session. */
export const groupPairsByTutor = (
  session: SessionWithParticipants,
): Map<string, StudentWithSource[]> => {
  const pairsByTutor = new Map<string, StudentWithSource[]>();

  if (!session.studentTutorPairs || session.studentTutorPairs.length === 0) {
    return pairsByTutor;
  }

  session.studentTutorPairs.forEach((pair) => {
    if (!pairsByTutor.has(pair.tutorUserId)) {
      pairsByTutor.set(pair.tutorUserId, []);
    }
    const studentParticipant = session.participants?.find(
      (p) => p.userId === pair.studentUserId && p.role === 'student',
    );
    if (studentParticipant?.user) {
      const tutorStudents = pairsByTutor.get(pair.tutorUserId);
      if (tutorStudents) {
        tutorStudents.push({
          student: studentParticipant.user,
          source: pair.assignmentSource,
        });
      }
    }
  });

  return pairsByTutor;
};

/** Attendance tallies; volunteers are excluded from the counts. */
export const countAttendanceByStatus = (
  participants: SessionWithParticipants['participants'],
): AttendanceCount => {
  const allParticipants = (participants || []).filter((p) => p.role !== 'volunteer');
  return {
    present: allParticipants.filter((p) => p.attendanceStatus === 'present').length,
    absent: allParticipants.filter((p) => p.attendanceStatus === 'absent').length,
    excused: allParticipants.filter((p) => p.attendanceStatus === 'excused').length,
    pending: allParticipants.filter((p) => p.attendanceStatus === 'pending' || !p.attendanceStatus)
      .length,
  };
};

/** Badge color for completed/confirmed vs any other status. */
export const getStatusColor = (status: string): 'success' | 'error' => {
  return status === 'completed' || status === 'confirmed' ? 'success' : 'error';
};

/** Bucket students by assignment source for the attendance modal. */
export const groupStudentsBySource = (studentsData: StudentWithSource[]) => {
  const studentsBySource = new Map<string, StudentUser[]>();
  studentsData.forEach(({ student, source }) => {
    if (!studentsBySource.has(source)) {
      studentsBySource.set(source, []);
    }
    const studentsForSource = studentsBySource.get(source);
    if (studentsForSource) {
      studentsForSource.push(student);
    }
  });
  return studentsBySource;
};

/** Student id → attendance status + justification. */
export const createStudentsAttendanceMap = (
  studentsData: StudentWithSource[],
  participants: SessionWithParticipants['participants'],
) => {
  const map = new Map<string, { status: AttendanceStatus; justification?: string | null }>();
  studentsData.forEach(({ student }) => {
    const studentParticipant = participants?.find(
      (p) => p.userId === student.id && p.role === 'student',
    );
    if (studentParticipant) {
      map.set(student.id, {
        status: (studentParticipant.attendanceStatus as AttendanceStatus) || 'pending',
        justification: studentParticipant.absenceJustification,
      });
    }
  });
  return map;
};

/** Split participants into tutors / students / volunteers and unmatched leftovers. */
export const deriveSessionParticipants = (session: SessionWithParticipants) => {
  const tutors = session.participants?.filter((p) => p.role === 'tutor') || [];
  const students = session.participants?.filter((p) => p.role === 'student') || [];
  const volunteers = session.participants?.filter((p) => p.role === 'volunteer') || [];
  const pairsByTutor = groupPairsByTutor(session);
  const matchedTutorIds = new Set(pairsByTutor.keys());
  const matchedStudentIds = new Set(session.studentTutorPairs?.map((p) => p.studentUserId) || []);

  return {
    tutors,
    students,
    volunteers,
    pairsByTutor,
    unmatchedTutors: tutors.filter((t) => !matchedTutorIds.has(t.userId)),
    unmatchedStudents: students.filter((s) => !matchedStudentIds.has(s.userId)),
    unmatchedVolunteers: volunteers.filter((v) => !matchedTutorIds.has(v.userId)),
    countByStatus: countAttendanceByStatus(session.participants),
    hasParticipants: tutors.length > 0 || students.length > 0,
    hasPairs: pairsByTutor.size > 0,
  };
};

/** Shared list-API filter fields used by both group and instance queries. */
function inPersonSessionSharedFilters(filters: SessionFilters) {
  return {
    statuses: expandNotCompletedSessionStatuses(
      filters.statuses.length > 0 ? filters.statuses : undefined,
    ),
    establishmentId: filters.establishmentId,
    subjectId: filters.subjectId,
    levelId: filters.levelId,
    search: filters.search || undefined,
    recurrenceType: filters.recurrenceType,
  };
}

/** Map UI filters to group-list API params (local date strings). */
export function buildInPersonSessionGroupListParams(
  filters: SessionFilters,
  options: { page: number; pageSize: number; types: string[] | undefined },
): ListSessionGroupsParams {
  const { dateFrom, dateTo } = getDateRangeFromFilters(filters);
  const shared = inPersonSessionSharedFilters(filters);
  return {
    page: options.page,
    pageSize: options.pageSize,
    types: options.types as never,
    statuses: shared.statuses as never,
    dateFrom: dateFrom ? toLocalDateString(dateFrom) : undefined,
    dateTo: dateTo ? toLocalDateString(dateTo) : undefined,
    establishmentId: shared.establishmentId,
    subjectId: shared.subjectId,
    levelId: shared.levelId,
    search: shared.search,
    recurrenceType: shared.recurrenceType,
    tutorId: filters.tutorId || undefined,
    animatorUserId: filters.animatorUserId || undefined,
  };
}

/** Map UI filters to standalone session-list API params. */
export function buildInPersonSessionListParams(
  filters: SessionFilters,
  options: { page: number; pageSize: number; types: string[] | undefined },
) {
  const { dateFrom, dateTo } = getDateRangeFromFilters(filters);
  const shared = inPersonSessionSharedFilters(filters);
  return {
    page: options.page,
    pageSize: options.pageSize,
    types: options.types,
    statuses: shared.statuses,
    dateFrom,
    dateTo,
    establishmentId: shared.establishmentId,
    subjectId: shared.subjectId,
    levelId: shared.levelId,
    search: shared.search,
    recurrenceType: shared.recurrenceType,
  };
}

/** First page of standalone information meetings (merge into the group list). */
export function buildInPersonStandaloneInformationListParams(filters: SessionFilters) {
  return buildInPersonSessionListParams(filters, {
    page: 1,
    pageSize: 100,
    types: [...SESSION_TAB_TO_TYPES['information-meeting']],
  });
}

/** Group-export params (same filters as the list, no pagination). */
export function buildInPersonSessionGroupExportParams(
  filters: SessionFilters,
  types: string[] | undefined,
): Omit<ListSessionGroupsParams, 'page' | 'pageSize'> {
  const { dateFrom, dateTo } = getDateRangeFromFilters(filters);
  const shared = inPersonSessionSharedFilters(filters);
  return {
    types: types as never,
    statuses: shared.statuses as never,
    dateFrom: dateFrom ? toLocalDateString(dateFrom) : undefined,
    dateTo: dateTo ? toLocalDateString(dateTo) : undefined,
    establishmentId: shared.establishmentId,
    subjectId: shared.subjectId,
    levelId: shared.levelId,
    search: shared.search,
    recurrenceType: shared.recurrenceType,
    tutorId: filters.tutorId || undefined,
    animatorUserId: filters.animatorUserId || undefined,
  };
}

/** True for in-person information-meeting session type. */
export function isInformationMeetingInPersonType(type: string | undefined): boolean {
  return type === 'information_meeting_in_person';
}

/** Info meetings cancel the session; other types cancel the group. */
export function resolveInPersonGroupCancelTarget(groupType: string): 'session' | 'group' {
  return isInformationMeetingInPersonType(groupType) ? 'session' : 'group';
}

/** Fetch the session GET when not in group mode, or for info meetings. */
export function shouldFetchExpandedSessionForGroup(
  useGroupMode: boolean,
  groupType?: string,
): boolean {
  return !useGroupMode || isInformationMeetingInPersonType(groupType);
}

/** Info meetings show the session GET (or first child); others show the group. */
export function resolveGroupDetailsSession(
  group: SessionGroup,
  expandedSessionData: SessionWithParticipants | undefined,
): SessionWithParticipants | SessionGroup {
  if (isInformationMeetingInPersonType(group.type)) {
    return (expandedSessionData ?? group.sessions[0]) as SessionWithParticipants;
  }
  return group;
}

/** Cancel-modal label: type + French session date. */
export function formatSessionListItemCancelName(session: {
  type: string;
  sessionDate: string | Date;
}): string {
  const typeLabel = SESSION_TYPE_CONFIG[session.type]?.label || session.type;
  return `${typeLabel} – ${new Date(session.sessionDate).toLocaleDateString('fr-FR')}`;
}

/** Standalone-session export params (same filters as the list, no pagination). */
export function buildInPersonSessionExportParams(
  filters: SessionFilters,
  types: string[] | undefined,
) {
  const { dateFrom, dateTo } = getDateRangeFromFilters(filters);
  const shared = inPersonSessionSharedFilters(filters);
  return {
    types,
    statuses: shared.statuses,
    dateFrom,
    dateTo,
    establishmentId: shared.establishmentId,
    subjectId: shared.subjectId,
    levelId: shared.levelId,
    search: shared.search,
    recurrenceType: shared.recurrenceType,
  };
}
